import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Labels {
  title: string;
  intro: string;
  reload: string;
}

interface Props {
  children: ReactNode;
  /**
   * Localized chrome for the fallback. Optional and defaulted, because the boundary
   * must be able to render even if the thing that threw was the i18n layer itself.
   */
  labels?: Labels;
}

interface State {
  error: Error | null;
}

const FALLBACK: Labels = {
  title: 'This page hit an error',
  intro:
    'The rest of the console is still working — switch to another page from the menu, or try again. Technical details:',
  reload: 'Try again',
};

/**
 * Contains a render-time exception to the page area.
 *
 * Without this, a single component throwing during render unmounts the whole React
 * tree and leaves a blank screen with no way back. Here the sidebar and header stay
 * mounted (the boundary wraps only the routed outlet), the operator can navigate
 * away, and the message that was thrown is shown so it can be reported without
 * opening the browser console.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The card shows the message; the console gets the full stack for diagnosis.
    console.error('Page render failed:', error, info.componentStack);
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const labels = this.props.labels ?? FALLBACK;
    return (
      <section className="card">
        <h1>{labels.title}</h1>
        <p>{labels.intro}</p>
        <pre className="code-block" style={{ whiteSpace: 'pre-wrap' }}>
          {error.message || String(error)}
        </pre>
        <button type="button" className="btn btn--primary" onClick={this.reset}>
          {labels.reload}
        </button>
      </section>
    );
  }
}
